import type {
  DOMConversionMap,
  DOMConversionOutput,
  DOMExportOutput,
  EditorConfig,
  LexicalNode,
  NodeKey,
  SerializedLexicalNode,
  Spread,
} from 'lexical'
import { $applyNodeReplacement, DecoratorNode } from 'lexical'
import React from 'react'

import { ImageComponent } from '../ImageComponent/ImageComponent'
import { getPureAltText } from '../../utils/getPureAltText'

export interface ImagePayload {
  altText: string
  height: string
  key?: NodeKey
  src: string
  width: string
}

const convertImageElement = (domNode: Node): null | DOMConversionOutput => {
  const img = domNode as HTMLImageElement
  if (img.src.startsWith('file:///')) {
    return null
  }
  const { alt: altText, src, width, height } = img
  const node = $createImageNode({ altText, height: `${height}`, src, width: `${width}` })
  return { node }
}

export type SerializedImageNode = Spread<
  {
    altText: string
    height: string
    src: string
    width: string
  },
  SerializedLexicalNode
>

/**
 * Node image with basic functionality
 */
export class ImageNode extends DecoratorNode<JSX.Element> {
  __src: string
  __altText: string
  __width: string
  __height: string

  static getType(): string {
    return 'image'
  }

  static clone(node: ImageNode): ImageNode {
    return new ImageNode(node.__src, node.__altText, node.__width, node.__height, node.__key)
  }

  static importJSON(serializedNode: SerializedImageNode): ImageNode {
    const { altText, height, width, src } = serializedNode
    const node = $createImageNode({
      altText,
      height,
      src,
      width,
    })

    return node
  }

  exportDOM(): DOMExportOutput {
    const element = document.createElement('img')
    element.setAttribute('src', this.__src)
    element.setAttribute('alt', this.__altText)
    element.setAttribute('width', this.__width.toString())
    element.setAttribute('height', this.__height.toString())
    return { element }
  }

  static importDOM(): DOMConversionMap | null {
    return {
      img: () => ({
        conversion: convertImageElement,
        priority: 0,
      }),
    }
  }

  constructor(src: string, altText: string, width: string, height: string, key?: NodeKey) {
    super(key)
    this.__src = src
    this.__altText = altText
    this.__width = width
    this.__height = height
  }

  exportJSON(): SerializedImageNode {
    return {
      altText: this.__altText,
      height: this.__height,
      src: this.__src,
      type: 'image',
      version: 1,
      width: this.__width,
    }
  }

  updateFullData({ width, src, altText, height }: ImagePayload): void {
    const writable = this.getWritable()
    writable.__width = width
    writable.__height = height
    writable.__src = src
    writable.__altText = altText
  }

  getFullData(): ImagePayload {
    return {
      altText: this.__altText,
      height: this.__height,
      src: this.__src,
      width: this.__width,
    }
  }

  // View

  createDOM(config: EditorConfig): HTMLElement {
    const span = document.createElement('span')
    const theme = config.theme
    const className = theme.image
    if (className !== undefined) {
      span.className = className
    }
    return span
  }

  updateDOM(): false {
    return false
  }

  decorate(): JSX.Element {
    return (
      <ImageComponent
        imageData={{
          src: this.__src,
          altText: getPureAltText(this.__altText),
          height: this.__height,
          width: this.__width,
        }}
        nodeKey={this.getKey()}
      />
    )
  }
}

export const $createImageNode = ({ altText, height, src, width, key }: ImagePayload): ImageNode => {
  return $applyNodeReplacement(new ImageNode(src, altText, width, height, key))
}

export const $isImageNode = (node: LexicalNode | null | undefined): node is ImageNode => {
  return node instanceof ImageNode
}
